import { memo, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import type { BundledLanguage } from 'shiki';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { Artifact } from './Artifact';
import { CodeBlock } from './CodeBlock';
import { AddStripeKeyButton } from '~/components/ui/AddStripeKey';
import { Collapsible } from '~/components/ui/Collapsible';
import { SettingsLink } from '~/components/ui/SettingsLink';
import { StaticHostingSetup, type StaticHostingSetupAction } from '~/components/ui/StaticHostingSetup';
import { UpdateStripeProducts } from '~/components/ui/UpdateStripeProducts';
import { UpgradeLink } from '~/components/ui/UpgradeLink';
import type { SettingsPage } from '~/lib/stores/menu';
import { allowedHTMLElements } from '~/utils/html';
import { createScopedLogger } from '~/utils/logger';
import { rehypePlugins, remarkPlugins } from '~/utils/markdown';

import styles from './Markdown.module.scss';

const BOLT_SETTINGS_LINK = '#__bolt-settings__';
const BOLT_SETUP_STRIPE_LINK = 'https://bolt.new/setup/stripe';
const BOLT_UPDATE_PRODUCTS_STRIPE_LINK = 'https://bolt.new/update/stripe';
const BOLT_SETUP_STATIC_HOSTING = 'https://bolt.new/setup/static-hosting';

const logger = createScopedLogger('MarkdownComponent');

interface MarkdownProps {
  children: string;
  className?: string;
  html?: boolean;
  limitedMarkdown?: boolean;
  scrollToBottom?: ScrollToBottom;
}

export const Markdown = memo(
  ({ className, children, html = false, limitedMarkdown = false, scrollToBottom }: MarkdownProps) => {
    logger.trace('Render');

    const components = useMemo(() => {
      return {
        div: ({ className, children, node, ...props }) => {
          if (className?.includes('__boltArtifact__')) {
            const artifactId = node?.properties.dataArtifactId as string;

            if (!artifactId) {
              logger.error(`Invalid artifact id ${artifactId}`);
            }

            return <Artifact id={artifactId} scrollToBottom={scrollToBottom} />;
          }

          if (className?.includes('__boltCollapsible__')) {
            const title = node?.properties.dataTitle as string;
            const icon = node?.properties.dataIcon as string | undefined;

            return (
              <Collapsible
                titleClassName="bg-bolt-elements-artifacts-background"
                toggleButtonClassName="bg-bolt-elements-artifacts-background"
                title={
                  <div className="flex items-center gap-2.5 p-3 grow text-bolt-elements-textPrimary leading-5 text-sm">
                    <span className={`${icon ?? 'i-ph:info'} size-4`} />
                    <span className="font-medium">{title}</span>
                  </div>
                }
                scrollToBottom={scrollToBottom}
              >
                <div className="p-5 bg-bolt-elements-artifacts-background">{children}</div>
              </Collapsible>
            );
          }

          return (
            <div className={className} {...props}>
              {children}
            </div>
          );
        },
        a: ({ node, children, className, ...props }) => {
          if (className?.includes('__boltUpgradePlan__')) {
            return <UpgradeLink />;
          }

          const href = props.href;

          if (href === BOLT_SETUP_STRIPE_LINK) {
            return <AddStripeKeyButton />;
          }

          if (href === BOLT_UPDATE_PRODUCTS_STRIPE_LINK) {
            return <UpdateStripeProducts />;
          }

          if (href?.startsWith(`${BOLT_SETUP_STATIC_HOSTING}/`)) {
            const action = href.slice(BOLT_SETUP_STATIC_HOSTING.length + 1) as StaticHostingSetupAction;

            return <StaticHostingSetup action={action}>{children}</StaticHostingSetup>;
          }

          if (href === BOLT_SETTINGS_LINK || href?.startsWith(`${BOLT_SETTINGS_LINK}/`)) {
            const link = href.startsWith(`${BOLT_SETTINGS_LINK}/`)
              ? href.slice(BOLT_SETTINGS_LINK.length + 1)
              : undefined;

            const [page, screen] = (link?.split('/') ?? [link, undefined]) as [
              SettingsPage | undefined,
              string | undefined,
            ];

            return (
              <SettingsLink page={page} screen={screen}>
                {children}
              </SettingsLink>
            );
          }

          return (
            <a {...props} target="_blank">
              {children}
            </a>
          );
        },
        pre: (props) => {
          const { children, node, ...rest } = props;

          const [firstChild] = node?.children ?? [];

          if (
            firstChild &&
            firstChild.type === 'element' &&
            firstChild.tagName === 'code' &&
            firstChild.children[0].type === 'text'
          ) {
            const { className, ...rest } = firstChild.properties;
            const [, language = 'plaintext'] = /language-(\w+)/.exec(String(className) || '') ?? [];
            const code = firstChild.children[0].value;

            return <CodeBlock code={code} language={language as BundledLanguage} {...rest} />;
          }

          return <pre {...rest}>{children}</pre>;
        },
      } satisfies Components;
    }, []);

    return (
      <ReactMarkdown
        allowedElements={allowedHTMLElements}
        className={className ?? styles.MarkdownContent}
        components={components}
        remarkPlugins={remarkPlugins(limitedMarkdown)}
        rehypePlugins={rehypePlugins(html)}
      >
        {children}
      </ReactMarkdown>
    );
  },
);
